import { useState } from "react";
import { RepertoirePicker } from '../Repertoire';

const C = {
  surface:"#181818", surface2:"#202020", border:"#2a2a2a",
  muted:"#666", orange:"#FF5F1F", magenta:"#E8197D",
  gold:"#F5A623", green:"#4ade80", white:"#fff", red:"#f87171", blue:"#60a5fa",
};

const card  = (x={}) => ({ background:C.surface, borderRadius:10, padding:16, border:`0.5px solid ${C.border}`, ...x });
const inp   = (x={}) => ({ background:C.surface2, border:`0.5px solid ${C.border}`, borderRadius:8, padding:"9px 12px", color:C.white, fontSize:13, outline:"none", width:"100%", boxSizing:"border-box", ...x });
const btn   = (v="primary",x={}) => ({ padding:"8px 16px", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer", border:"none", boxSizing:"border-box", ...(v==="primary"?{background:`linear-gradient(135deg,${C.orange},${C.magenta})`,color:C.white}:{}), ...(v==="ghost"?{background:"none",border:`0.5px solid ${C.border}`,color:C.muted}:{}), ...(v==="danger"?{background:"#3d1515",color:C.red,border:`0.5px solid #f8717133`}:{}), ...x });
const lbl   = t => <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>{t}</div>;

const roleCfg = {
  danser:   { label:"Danser",   color:"#E8197D" },
  vokalist: { label:"Vokalist", color:"#a78bfa" },
  tm:       { label:"TM",       color:"#F5A623" },
  akrobat:  { label:"Akrobat",  color:"#FF5F1F" },
  musiker:  { label:"Musiker",  color:"#60a5fa" },
  tekniker: { label:"Tekniker", color:"#94a3b8" },
  admin:    { label:"Admin",    color:"#34d399" },
};

function Avatar({ initials, role, size=28 }) {
  const color = roleCfg[role]?.color || C.orange;
  return (
    <div style={{width:size,height:size,borderRadius:"50%",background:`${color}22`,border:`1px solid ${color}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size>28?12:10,color,fontWeight:500,flexShrink:0}}>
      {initials}
    </div>
  );
}

// ── Skjema for å redigere ett show-nummer ──
function ShowItemForm({ initial, teamUsers, onSave, onCancel, nextOrder }) {
  const [title,      setTitle]      = useState(initial?.title||"");
  const [duration,   setDuration]   = useState(initial?.duration||"");
  const [order,      setOrder]      = useState(initial?.order ?? nextOrder ?? 1);
  const [persons,    setPersons]    = useState(initial?.persons||[]);
  const [costume,    setCostume]    = useState(initial?.costume||"");
  const [hairMakeup, setHairMakeup] = useState(initial?.hair_makeup||"");
  const [notes,      setNotes]      = useState(initial?.notes||"");
  const [showPicker, setShowPicker] = useState(false);

  const togglePerson = id => setPersons(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);

  const save = () => {
    if(!title.trim()) return;
    onSave({
      ...(initial||{}),
      id: initial?.id || Date.now(),
      order,
      title: title.trim(),
      duration: duration.trim(),
      persons,
      costume: costume.trim(),
      hair_makeup: hairMakeup.trim(),
      notes: notes.trim(),
    });
  };

  return (
    <div style={{padding:14, background:C.surface2, borderRadius:8, border:`0.5px solid ${C.orange}44`, marginBottom:8}}>
      {showPicker&&<RepertoirePicker onSelect={item=>{ setTitle(item.title); setDuration(item.duration||""); setCostume(item.costume||""); setHairMakeup(item.hair_makeup||""); setNotes(item.rep_notes||""); }} onClose={()=>setShowPicker(false)}/>}
      {/* Show-nummer + Tittel + varighet */}
      <div style={{display:"grid", gridTemplateColumns:"60px 1fr auto", gap:8, marginBottom:8}}>
        <div>
          {lbl("Show #")}
          <input style={inp({height:44,boxSizing:"border-box"})} type="number" min="1" value={order} onChange={e=>setOrder(Number(e.target.value))} placeholder="1"/>
        </div>
        <div>
          {lbl("Tittel *")}
          <input style={inp({height:44,boxSizing:"border-box"})} value={title} onChange={e=>setTitle(e.target.value)} placeholder="Show-tittel / nummer..." onKeyDown={e=>e.key==="Enter"&&save()}/>
        </div>
        <div>
          {lbl("Varighet")}
          <input style={inp({height:44,boxSizing:"border-box",width:90})} value={duration} onChange={e=>setDuration(e.target.value)} placeholder="4:30"/>
        </div>
      </div>
      <button style={{...btn("ghost",{fontSize:11,padding:"4px 12px",marginBottom:12}),borderColor:`${C.orange}44`,color:C.orange}} onClick={()=>setShowPicker(true)}>
        🎵 Hent fra repertoire
      </button>

      {/* Hvem er med */}
      {teamUsers.length > 0 && (
        <div style={{marginBottom:12}}>
          {lbl("Hvem er med")}
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
            {teamUsers.map(u=>{
              const selected = persons.includes(u.id);
              const color = roleCfg[u.role]?.color || C.orange;
              return (
                <div key={u.id} onClick={()=>togglePerson(u.id)} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,cursor:"pointer",
                  background: selected ? `${color}22` : "transparent",
                  border: `0.5px solid ${selected ? color : C.border}`,
                  color: selected ? color : C.muted,
                  fontSize:12, fontWeight:500}}>
                  <Avatar initials={u.initials} role={u.role} size={20}/>
                  {u.name.split(" ")[0]}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kostyme */}
      <div style={{marginBottom:12}}>
        {lbl("Kostyme")}
        <textarea style={inp({minHeight:60,resize:"vertical"})} value={costume} onChange={e=>setCostume(e.target.value)} placeholder="Beskriv kostymet for dette nummeret..."/>
      </div>

      {/* Hår og sminke */}
      <div style={{marginBottom:12}}>
        {lbl("Hår og sminke")}
        <textarea style={inp({minHeight:60,resize:"vertical"})} value={hairMakeup} onChange={e=>setHairMakeup(e.target.value)} placeholder="Hårfrisyre, sminke, instruksjoner..."/>
      </div>

      {/* Notater */}
      <div style={{marginBottom:14}}>
        {lbl("Notater")}
        <textarea style={inp({minHeight:60,resize:"vertical"})} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Andre instruksjoner, koreograf, musikk-link..."/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button style={btn("ghost",{flex:1})} onClick={onCancel}>Avbryt</button>
        <button style={btn("primary",{flex:1})} onClick={save}>Lagre</button>
      </div>
    </div>
  );
}

// ── Visning av ett show-nummer (ikke-redigering) ──
function ShowItemView({ item, index, teamUsers, expanded, onToggle, onEdit, onDelete, onMoveUp, onMoveDown, isFirst, isLast, readOnly=false }) {
  const members = teamUsers.filter(u => (item.persons||[]).includes(u.id));

  return (
    <div style={{marginBottom:8}}>
      <div
        onClick={onToggle}
        style={{...card({padding:"12px 14px", cursor:"pointer", marginBottom:0}), borderLeft:`3px solid ${C.orange}`}}
      >
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {/* Nummer */}
          <div style={{fontSize:12,color:C.muted,fontWeight:600,minWidth:24,flexShrink:0}}>#{item.order ?? index+1}</div>

          {/* Tittel + varighet */}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:500,color:C.white,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.title}</div>
            {item.duration&&<div style={{fontSize:11,color:C.muted,marginTop:1}}>{item.duration}</div>}
          </div>

          {/* Avatarer */}
          {members.length>0&&(
            <div style={{display:"flex",gap:-4}}>
              {members.slice(0,4).map(u=><Avatar key={u.id} initials={u.initials} role={u.role} size={24}/>)}
              {members.length>4&&<div style={{width:24,height:24,borderRadius:"50%",background:C.surface2,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:C.muted}}>+{members.length-4}</div>}
            </div>
          )}

          {/* Pil */}
          <div style={{fontSize:10,color:C.muted,transition:"transform 0.2s",transform:expanded?"rotate(180deg)":"rotate(0deg)",flexShrink:0}}>▾</div>
        </div>

        {/* Ekspandert innhold */}
        {expanded&&(
          <div style={{marginTop:12,paddingTop:12,borderTop:`0.5px solid ${C.border}`}}>

            {/* Hvem er med */}
            {members.length>0&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:6,textTransform:"uppercase",letterSpacing:0.5}}>Hvem er med</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {members.map(u=>{
                    const color = roleCfg[u.role]?.color || C.orange;
                    return (
                      <div key={u.id} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:20,background:`${color}22`,border:`0.5px solid ${color}44`,fontSize:12,color}}>
                        <Avatar initials={u.initials} role={u.role} size={18}/>
                        {u.name.split(" ")[0]}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Kostyme */}
            {item.costume&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>👗 Kostyme</div>
                <div style={{fontSize:13,color:"#ccc",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{item.costume}</div>
              </div>
            )}

            {/* Hår og sminke */}
            {item.hair_makeup&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>💄 Hår og sminke</div>
                <div style={{fontSize:13,color:"#ccc",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{item.hair_makeup}</div>
              </div>
            )}

            {/* Notater */}
            {item.notes&&(
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:0.5}}>📝 Notater</div>
                <div style={{fontSize:13,color:"#ccc",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{item.notes}</div>
              </div>
            )}

            {!item.costume&&!item.hair_makeup&&!item.notes&&members.length===0&&(
              <div style={{fontSize:13,color:"#444"}}>Ingen detaljer lagt til ennå.</div>
            )}

            {/* Rediger/flytt/slett */}
            {!readOnly&&<div style={{display:"flex",gap:6,marginTop:12,flexWrap:"wrap"}}>
              <button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onEdit();}}>✎ Rediger</button>
              {!isFirst&&<button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onMoveUp();}}>↑</button>}
              {!isLast&&<button style={btn("ghost",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onMoveDown();}}>↓</button>}
              <button style={btn("danger",{padding:"5px 10px",fontSize:11})} onClick={e=>{e.stopPropagation();onDelete();}}>Slett</button>
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Read-only visning ──
export function ShowProgramViewer({ items, teamUsers=[] }) {
  const [expandedId, setExpandedId] = useState(null);
  if(!items||items.length===0) return <div style={{fontSize:13,color:"#444"}}>Ingen show-numre lagt til.</div>;
  return (
    <div>
      {items.map((item,i)=>(
        <ShowItemView
          key={item.id}
          item={item}
          index={i}
          teamUsers={teamUsers}
          expanded={expandedId===item.id}
          onToggle={()=>setExpandedId(expandedId===item.id?null:item.id)}
          onEdit={()=>{}}
          onDelete={()=>{}}
          onMoveUp={()=>{}}
          onMoveDown={()=>{}}
          isFirst={true}
          isLast={true}
        />
      ))}
    </div>
  );
}

// ── Hoved-editor ──
export function ShowProgramEditor({ items, onChange, teamUsers=[] }) {
  const [editingId,  setEditingId]  = useState(null);
  const [showAddForm,setShowAddForm]= useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const save = (item) => {
    const exists = items.find(i=>i.id===item.id);
    onChange(exists ? items.map(i=>i.id===item.id?item:i) : [...items, item]);
    setEditingId(null);
    setShowAddForm(false);
    setExpandedId(item.id);
  };

  const remove = id => onChange(items.filter(i=>i.id!==id));

  const moveUp = idx => {
    if(idx===0) return;
    const arr = [...items];
    [arr[idx-1],arr[idx]] = [arr[idx],arr[idx-1]];
    onChange(arr);
  };

  const moveDown = idx => {
    if(idx===items.length-1) return;
    const arr = [...items];
    [arr[idx],arr[idx+1]] = [arr[idx+1],arr[idx]];
    onChange(arr);
  };

  return (
    <div>
      {items.map((item,i)=>(
        <div key={item.id}>
          {editingId===item.id
            ? <ShowItemForm
                initial={item}
                teamUsers={teamUsers}
                onSave={save}
                onCancel={()=>setEditingId(null)}
              />
            : <ShowItemView
                item={item}
                index={i}
                teamUsers={teamUsers}
                expanded={expandedId===item.id}
                onToggle={()=>setExpandedId(expandedId===item.id?null:item.id)}
                onEdit={()=>{setEditingId(item.id);setExpandedId(null);}}
                onDelete={()=>remove(item.id)}
                onMoveUp={()=>moveUp(i)}
                onMoveDown={()=>moveDown(i)}
                isFirst={i===0}
                isLast={i===items.length-1}
              />
          }
        </div>
      ))}

      {showAddForm
        ? <ShowItemForm
            teamUsers={teamUsers}
            onSave={save}
            onCancel={()=>setShowAddForm(false)}
            nextOrder={items.length+1}
          />
        : <button style={{...btn("ghost"),width:"100%",marginTop:4}} onClick={()=>setShowAddForm(true)}>+ Legg til show-nummer</button>
      }
    </div>
  );
}